// Kalkylator-logik baserad på abonnemangspaketen Lite/Core/Pro.
// Tier-kortet (PricingTierSwitcher) och kalkylatorn (QuoteCalculator)
// läser bägge från PACKAGES + UPGRADES så priserna aldrig kan drifta isär.
//
// Anchor (samma som tier-kortet, KVARTAL = normalpris):
//   Lite  = 14 995 kr/mån (kvartal)
//   Core  = 34 995 kr/mån (kvartal)
//   Pro   = 45 995 kr/mån (kvartal)
//
// Bindning justerar månadspriset:
//   Månad  = +25 % (kortare bindning, högre pris)
//   Kvartal=  0 %  (normalpris)
//   Halvår = −10 %
//   År     = −15 %

export type Period = "manad" | "kvartal" | "halvar" | "ar";

export type ChannelId =
  | "linkedin"
  | "youtube"
  | "tiktok"
  | "instagram"
  | "webb"
  | "tv";

export type FormatId = "16_9" | "9_16" | "1_1" | "3_4";

// Manus & regi ingår i alla paket → finns ej som tillägg.
export type AddonId = "undertext" | "annons";

export type PackageId = "lite" | "core" | "pro";

// ---- Paket (sanningskälla för båda UI:erna) ---------------------------

export interface PackageDef {
  id: PackageId;
  name: string;
  priceMonthly: number;
  clipsIncluded: number;
  formatsIncluded: FormatId[];
  revisions: number;
  recordingCadence: string;
  manusLevel: string;
  desc: string;
  badge?: string;
}

export const PACKAGES: Record<PackageId, PackageDef> = {
  lite: {
    id: "lite",
    name: "Pulse Lite",
    priceMonthly: 14_995,
    clipsIncluded: 2,
    formatsIncluded: ["16_9", "9_16"],
    revisions: 2,
    recordingCadence: "1 inspelningsdag / kvartal",
    manusLevel: "Planeringsmöte + enklare manusplan",
    desc: "Bygg en jämn takt på två kanaler.",
  },
  core: {
    id: "core",
    name: "Pulse Core",
    priceMonthly: 34_995,
    clipsIncluded: 4,
    formatsIncluded: ["16_9", "9_16", "3_4"],
    revisions: 2,
    recordingCadence: "1 inspelningsdag / varannan månad",
    manusLevel: "Fullt produktionsmöte + manus & regi",
    desc: "Äg flödet — contentmaskineri i flera format.",
    badge: "Mest populär",
  },
  pro: {
    id: "pro",
    name: "Pulse Pro",
    priceMonthly: 45_995,
    clipsIncluded: 6,
    formatsIncluded: ["16_9", "9_16", "3_4", "1_1"],
    revisions: 4,
    recordingCadence: "1 inspelningsdag / månad",
    manusLevel: "Strategisk workshop, manus & kreativ ledning",
    desc: "Dominera er kategori med kreativ höjd varje månad.",
  },
};

// ---- Uppgraderings­priser ----------------------------------------------

export const UPGRADES = {
  /** Per extra klipp/mån utöver paketets bas */
  extraClipRate: 1_000,
  /** Per extra inspelningsdag */
  extraFilmdayRate: 8_000,
  /** Månadspris för valbara tillägg (manus & regi ingår i alla paket) */
  addonsMonthly: {
    undertext: 2_200,
    annons: 3_800,
  } satisfies Record<AddonId, number>,
} as const;

// ---- Periodregler (samma i kalkylator OCH tier-kort) -------------------
// Negativt = rabatt, positivt = premium. Kvartal = normalpris.

export const PRICING = {
  periodAdjustment: {
    manad: 0.25,
    kvartal: 0,
    halvar: -0.10,
    ar: -0.15,
  } satisfies Record<Period, number>,
  monthsInPeriod: {
    manad: 1,
    kvartal: 3,
    halvar: 6,
    ar: 12,
  } satisfies Record<Period, number>,
} as const;

// ---- UI-metadata --------------------------------------------------------

// Kanal → naturligt format. Används endast för att rekommendera paket.
export const CHANNEL_FORMAT: Record<ChannelId, FormatId> = {
  linkedin: "1_1",
  youtube: "16_9",
  tiktok: "9_16",
  instagram: "9_16",
  webb: "16_9",
  tv: "16_9",
};

export const CHANNELS: { id: ChannelId; label: string; format: FormatId }[] = [
  { id: "linkedin", label: "LinkedIn", format: "1_1" },
  { id: "youtube", label: "YouTube", format: "16_9" },
  { id: "tiktok", label: "TikTok", format: "9_16" },
  { id: "instagram", label: "Instagram", format: "9_16" },
  { id: "webb", label: "Webb / landningssidor", format: "16_9" },
  { id: "tv", label: "Linjär / streaming", format: "16_9" },
];

export const PERIODS: { id: Period; label: string; short: string }[] = [
  { id: "manad", label: "Månadsvis", short: "Månad" },
  { id: "kvartal", label: "Per kvartal", short: "Kvartal" },
  { id: "halvar", label: "Per halvår", short: "Halvår" },
  { id: "ar", label: "Per år", short: "År" },
];

export const FORMATS: { id: FormatId; label: string; ratio: string }[] = [
  { id: "16_9", label: "Liggande", ratio: "16:9" },
  { id: "9_16", label: "Vertikalt", ratio: "9:16" },
  { id: "1_1", label: "Kvadrat", ratio: "1:1" },
  { id: "3_4", label: "Stående", ratio: "3:4" },
];

export const ADDONS: { id: AddonId; label: string; help: string }[] = [
  {
    id: "undertext",
    label: "Undertext + översättning",
    help: "Bränd undertext på svenska + en valbar översättning per klipp.",
  },
  {
    id: "annons",
    label: "Annonsversioner / A/B",
    help: "2–3 hookvarianter per klipp anpassade för annonsering.",
  },
];

// ---- Räknemotor --------------------------------------------------------

export interface QuoteLineItem {
  id: string;
  label: string;
  detail?: string;
  amount: number;
  /** "ingår i paket" eller "tillägg" */
  kind: "package" | "upgrade";
}

export interface QuoteInput {
  packageId: PackageId;
  /** Extra klipp/mån utöver paketets bas (0 om inget) */
  extraClips: number;
  /** Extra inspelningsdagar utöver paketets kadens */
  extraFilmdays: number;
  addons: AddonId[];
  period: Period;
}

export interface QuoteBreakdown {
  packageId: PackageId;
  packageName: string;
  period: Period;
  monthsInPeriod: number;
  clipsPerMonth: number;
  clipsTotal: number;
  baseFilmdays: number;
  extraFilmdays: number;
  formatsIncluded: FormatId[];
  revisions: number;
  recordingCadence: string;
  subtotal: number;
  /** Negativt = rabatt, positivt = premium */
  adjustmentPct: number;
  adjustmentAmount: number;
  total: number;
  monthlyAverage: number;
  lineItems: QuoteLineItem[];
}

/** Härleder rekommenderat paket från valda kanaler — endast som hint. */
export function recommendPackageForChannels(channels: ChannelId[]): PackageId {
  const formats = new Set<FormatId>();
  for (const c of channels) formats.add(CHANNEL_FORMAT[c]);
  if (formats.size <= 2 && channels.length <= 2) return "lite";
  if (formats.size <= 3) return "core";
  return "pro";
}

/** Basfilmdagar/mån enligt paketets kadens. */
export function basePerMonthFilmdays(packageId: PackageId): number {
  switch (packageId) {
    case "lite":
      return 1 / 3; // 1 dag/kvartal
    case "core":
      return 1 / 2; // 1 dag / varannan månad
    case "pro":
      return 1; // 1 dag/månad
  }
}

export function calculateQuote(input: QuoteInput): QuoteBreakdown {
  const pkg = PACKAGES[input.packageId];
  const months = PRICING.monthsInPeriod[input.period];
  const clipsPerMonth = pkg.clipsIncluded + Math.max(0, input.extraClips);
  const clipsTotal = clipsPerMonth * months;
  const baseFilmdays = Math.max(1, Math.round(basePerMonthFilmdays(input.packageId) * months));
  const extraFilmdays = Math.max(0, input.extraFilmdays);

  const lineItems: QuoteLineItem[] = [
    {
      id: "package",
      label: `${pkg.name} – baspaket`,
      detail: `${pkg.clipsIncluded} klipp/mån · ${pkg.formatsIncluded
        .map((f) => f.replace("_", ":"))
        .join(", ")} · ${pkg.revisions} revisioner · ${pkg.recordingCadence}`,
      amount: pkg.priceMonthly * months,
      kind: "package",
    },
  ];

  // Extra klipp/mån
  if (input.extraClips > 0) {
    lineItems.push({
      id: "extra-clips",
      label: `+${input.extraClips} extra klipp/mån`,
      detail: `${UPGRADES.extraClipRate.toLocaleString("sv-SE")} kr/klipp × ${input.extraClips} × ${months} mån`,
      amount: UPGRADES.extraClipRate * input.extraClips * months,
      kind: "upgrade",
    });
  }

  // Extra filmdagar
  if (extraFilmdays > 0) {
    lineItems.push({
      id: "extra-filmdays",
      label: `+${extraFilmdays} extra filmdag${extraFilmdays === 1 ? "" : "ar"}`,
      detail: `${UPGRADES.extraFilmdayRate.toLocaleString("sv-SE")} kr/dag × ${extraFilmdays}`,
      amount: UPGRADES.extraFilmdayRate * extraFilmdays,
      kind: "upgrade",
    });
  }

  // Addons (månadsvis × antal månader)
  for (const a of input.addons) {
    const monthly = UPGRADES.addonsMonthly[a];
    const meta = ADDONS.find((x) => x.id === a);
    lineItems.push({
      id: `addon-${a}`,
      label: meta?.label ?? a,
      detail: `${monthly.toLocaleString("sv-SE")} kr/mån × ${months} mån`,
      amount: monthly * months,
      kind: "upgrade",
    });
  }

  const subtotal = lineItems.reduce((s, l) => s + l.amount, 0);
  const adjustmentPct = PRICING.periodAdjustment[input.period];
  const adjustmentAmount = subtotal * adjustmentPct;
  const total = subtotal + adjustmentAmount;
  const monthlyAverage = total / months;

  return {
    packageId: input.packageId,
    packageName: pkg.name,
    period: input.period,
    monthsInPeriod: months,
    clipsPerMonth,
    clipsTotal,
    baseFilmdays,
    extraFilmdays,
    formatsIncluded: pkg.formatsIncluded,
    revisions: pkg.revisions,
    recordingCadence: pkg.recordingCadence,
    subtotal,
    adjustmentPct,
    adjustmentAmount,
    total,
    monthlyAverage,
    lineItems,
  };
}

export function formatCurrency(n: number): string {
  return new Intl.NumberFormat("sv-SE", {
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}
